import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit'
import { Redux } from '../..'
import { GygQueryOptions } from '@/src/views/components/master-pricing-list/types'

export interface RecipeEquipment {
  id: string
  name: string
  description?: string
  imageUrl?: string
  instructions?: string
  isActive?: boolean
}

export interface RecipeEquipmentResponse {
  data: RecipeEquipment[]
  meta: {
    total: number
    pages: number
  }
}

interface GygRecipeEquipmentState {
  equipments: RecipeEquipment[]
  loading: boolean
  error: string | null
  success: boolean | null
}

const initialState: GygRecipeEquipmentState = {
  equipments: [],
  loading: false,
  error: null,
  success: false
}

const BASE_URL = '/recipe-guide-equipments'

export const createRecipeEquipment = createAsyncThunk<RecipeEquipment, RecipeEquipment, Redux>(
  'gygRecipeEquipment/create',
  async (recipeEquipment, { extra }) => {
    const { data: responseData } = await extra.httpClient.post<RecipeEquipment>(`${BASE_URL}`, recipeEquipment)

    return responseData
  }
)

export const updateRecipeEquipment = createAsyncThunk<RecipeEquipment, RecipeEquipment, Redux>(
  'gygRecipeEquipment/update',
  async (recipeEquipment, { extra }) => {
    const { id, ...rest } = recipeEquipment
    const { data: responseData } = await extra.httpClient.put<RecipeEquipment>(`${BASE_URL}/${id}`, rest)

    return responseData
  }
)

export const getRecipeEquipment = createAsyncThunk<RecipeEquipment, string, Redux>(
  'gygRecipeEquipment/getOne',
  async (id: string, { extra }) => {
    const { data: responseData } = await extra.httpClient.get<RecipeEquipment>(`${BASE_URL}/${id}`)

    return responseData
  }
)

export const getAllRecipeEquipments = createAsyncThunk<RecipeEquipmentResponse, GygQueryOptions, Redux>(
  'gygRecipeEquipment/getAll',
  async (queryOptions, { extra }) => {
    const { data: responseData } = await extra.httpClient.get<RecipeEquipmentResponse>(`${BASE_URL}`, {
      params: queryOptions
    })

    return responseData
  }
)

const gygRecipeEquipmentSlice = createSlice({
  name: 'gygRecipeEquipment',
  initialState,
  reducers: {},
  extraReducers: builder => {
    builder
      .addCase(getAllRecipeEquipments.pending, state => {
        state.loading = true
        state.error = null
        state.success = null
      })
      .addCase(getAllRecipeEquipments.fulfilled, (state, action: PayloadAction<RecipeEquipmentResponse>) => {
        state.equipments = action.payload.data
        state.loading = false
        state.success = true
      })
      .addCase(getAllRecipeEquipments.rejected, (state, action) => {
        state.loading = false
        state.error = action.error.message || 'Failed to fetch recipe equipments'
        state.success = false
      })
      .addCase(createRecipeEquipment.pending, state => {
        state.loading = true
        state.error = null
        state.success = null
      })
      .addCase(createRecipeEquipment.fulfilled, (state, action: PayloadAction<RecipeEquipment>) => {
        state.equipments.push(action.payload)
        state.loading = false
        state.success = true
      })
      .addCase(createRecipeEquipment.rejected, (state, action) => {
        state.loading = false
        state.error = action.error.message || 'Failed to create recipe equipment'
        state.success = false
      })
      .addCase(updateRecipeEquipment.pending, state => {
        state.loading = true
        state.error = null
        state.success = null
      })
      .addCase(updateRecipeEquipment.fulfilled, (state, action: PayloadAction<RecipeEquipment>) => {
        const index = state.equipments.findIndex(e => e.id === action.payload.id)
        if (index !== -1) {
          state.equipments[index] = action.payload
        }
        state.loading = false
        state.success = true
      })
      .addCase(updateRecipeEquipment.rejected, (state, action) => {
        state.loading = false
        state.error = action.error.message || 'Failed to update recipe equipment'
        state.success = false
      })
      .addCase(getRecipeEquipment.pending, state => {
        state.loading = true
        state.error = null
        state.success = null
      })
      .addCase(getRecipeEquipment.fulfilled, (state, action: PayloadAction<RecipeEquipment>) => {
        const index = state.equipments.findIndex(e => e.id === action.payload.id)
        if (index !== -1) {
          state.equipments[index] = action.payload
        }
        state.loading = false
        state.success = true
      })
      .addCase(getRecipeEquipment.rejected, (state, action) => {
        state.loading = false
        state.error = action.error.message || 'Failed to fetch recipe equipment'
        state.success = false
      })
  }
})

export default gygRecipeEquipmentSlice.reducer
